/**
 * Makes PostgREST schema errors impossible to miss.
 *
 * Background: `docs/fixes/lead-map-orphaned-by-pins-rename.md`. The `leads` table was renamed
 * to `pins` without a compatibility view. Every query on `/app/map` started returning
 * `42P01 relation "public.leads" does not exist`. The calling hook caught the error, logged
 * it, and left its state as `[]`, so the page rendered as an ordinary empty map. It stayed
 * that way for months. Typecheck and the unit suite were green throughout, because a table
 * name is a runtime string, not a type.
 *
 * The distinction this module turns on:
 *
 *   A schema error is ALWAYS a bug and NEVER a legitimate empty result.
 *
 * That is not true of an RLS denial, an expired JWT, or a network blip — for those,
 * degrading to an empty state is often exactly right, and a caller that swallows them is
 * behaving correctly. So the guard escalates the first class and stays out of the way of
 * the second. Narrow on purpose: a guard that cried wolf on RLS would be turned off.
 *
 * Escalation deliberately does not run through the caller's promise. The caller's `catch`
 * is what hid the original defect, so reporting must escape it — see `reportSchemaError`.
 */

/** PostgREST/Postgres error codes that always indicate a broken query, never missing data. */
export const SCHEMA_ERROR_CODES = {
  "42P01": "undefined table",
  "42703": "undefined column",
  "42883": "undefined function",
} as const;

export type SchemaErrorCode = keyof typeof SCHEMA_ERROR_CODES;

export interface SchemaError {
  code: SchemaErrorCode;
  kind: (typeof SCHEMA_ERROR_CODES)[SchemaErrorCode];
  message: string;
}

const isSchemaErrorCode = (value: unknown): value is SchemaErrorCode =>
  typeof value === "string" && value in SCHEMA_ERROR_CODES;

/**
 * Classify a failed response body. Returns null for anything that is not a schema error,
 * including bodies that are not JSON at all — a proxy's HTML 502 must not become a second
 * failure on top of the first.
 *
 * `status` is accepted for call-site clarity but deliberately not branched on: PostgREST
 * returns 404 for `42P01`/`42883` and 400 for `42703`, and pinning those pairings would
 * make the guard silently stop working if they ever changed. The code alone is the signal.
 */
export function parseSchemaError(status: number, bodyText: string): SchemaError | null {
  void status;

  let body: unknown;
  try {
    body = JSON.parse(bodyText);
  } catch {
    return null;
  }

  if (typeof body !== "object" || body === null || Array.isArray(body)) return null;

  const code = (body as { code?: unknown }).code;
  if (!isSchemaErrorCode(code)) return null;

  const rawMessage = (body as { message?: unknown }).message;

  return {
    code,
    kind: SCHEMA_ERROR_CODES[code],
    message: typeof rawMessage === "string" ? rawMessage : "",
  };
}

/**
 * Render a request as `METHOD /path` for logging.
 *
 * The query string is dropped on purpose. PostgREST filters carry real values — an
 * `?email=eq.…` is PII and some callers put credentials in the query — and the relation
 * name we actually need is already in the server's own message.
 */
export function describeRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined
): string {
  const rawUrl =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;

  const method = init?.method ?? (input instanceof Request ? input.method : "GET");

  let path: string;
  try {
    path = new URL(rawUrl).pathname;
  } catch {
    path = rawUrl;
  }

  return `${method} ${path}`;
}

/** The message a developer sees. Says outright what the failure is not, to pre-empt the misread. */
export function formatSchemaError(error: SchemaError, request: string): string {
  return [
    `[supabase] ${error.code} (${error.kind}) on ${request}`,
    error.message,
    "This is a broken query, not an empty result. The relation, column, or function the app asked for does not exist in the database.",
  ]
    .filter(Boolean)
    .join("\n");
}

/**
 * Wrap a fetch implementation so schema errors are handed to `onSchemaError`.
 *
 * Contract, in order of importance:
 *  1. Never change what the caller receives. The response is returned untouched and its
 *     body stays unread — classification works on a clone.
 *  2. Never turn one failure into two. Any error raised while inspecting or reporting is
 *     swallowed; diagnostics must not break the request they describe.
 *  3. Only touch failed responses. Cloning every 200 would double memory on large payloads.
 */
export function withSchemaErrorGuard(
  fetchImpl: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  onSchemaError: (error: SchemaError, request: string) => void
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return async (input, init) => {
    const response = await fetchImpl(input, init);

    if (response.ok) return response;

    try {
      const bodyText = await response.clone().text();
      const schemaError = parseSchemaError(response.status, bodyText);
      if (schemaError) {
        onSchemaError(schemaError, describeRequest(input, init));
      }
    } catch {
      // Unreadable body, a clone that failed, or a reporter that threw. The response is
      // what the caller asked for; hand it back regardless.
    }

    return response;
  };
}

/**
 * Production reporter: log loudly, and in dev re-throw asynchronously.
 *
 * The async re-throw is the part that matters. Throwing inside the fetch chain would be
 * caught by the very `catch` that hid the original defect; rescheduling it onto a fresh
 * task puts it in front of `window.onerror` and the Vite overlay instead, where it cannot
 * be swallowed by application code.
 *
 * Dev-only, because in production an unhandled error would be a worse outcome for a user
 * than the degraded page — there the console line and any error reporter have to carry it.
 */
export function reportSchemaError(error: SchemaError, request: string): void {
  const message = formatSchemaError(error, request);

  console.error(message);

  if (import.meta.env.DEV) {
    setTimeout(() => {
      throw new Error(message);
    }, 0);
  }
}
